import React from 'react'
import { Link } from 'react-router-dom'
import { SettingsButton, SettingsCard, SettingsTitle } from '../../../components/settings/SettingsUI'
import WithdrawalStatusBanner from '../../../components/settings/WithdrawalStatusBanner'
import { money, useCreatorTools } from '../../../hooks/useCreatorTools'
import { createStripeConnectOnboarding } from '../../../api/stripeConnect'

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max <= 0 ? 0 : Math.max(0, Math.min(100, (value / max) * 100))
  return (
    <div className="w-full h-2 rounded-full bg-white/5 border border-white/10 overflow-hidden">
      <div className="h-full bg-white/70" style={{ width: `${pct}%` }} />
    </div>
  )
}

export default function CreatorPixePublish() {
  const DEV = import.meta.env.DEV
  const { plan, status, meta, earnings, payoutsEnabled, withdrawGate, devSetPlan, refresh } = useCreatorTools()

  const isCreator = plan !== 'none'
  const isPro = plan === 'creator_pro'

  return (
    <div className="space-y-4">
      <SettingsTitle
        title="Pixe Publish"
        subtitle="Creator plans, payouts, and vesting."
        right={
          DEV ? (
            <div className="flex items-center gap-2">
              <SettingsButton variant="ghost" onClick={() => devSetPlan('none')}>
                Set User
              </SettingsButton>
              <SettingsButton variant="ghost" onClick={() => devSetPlan('creator')}>
                Set Creator
              </SettingsButton>
              <SettingsButton variant="ghost" onClick={() => devSetPlan('creator_pro')}>
                Set Pro
              </SettingsButton>
            </div>
          ) : null
        }
      />

      <SettingsCard
        accent="key"
        title="Pixe Publish"
        subtitle="PIXE gifts are support. They generate earnings paid out in USD."
        right={<span className={`text-xs px-2 py-1 rounded-full border ${status.cls}`}>{status.label}</span>}
      >
        <div className="grid gap-4 md:grid-cols-3">
          <div className="md:col-span-2">
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm font-semibold">Status</div>
                <div className="mt-1 text-sm text-white/70">
                  {plan === 'none'
                    ? 'Not a creator yet.'
                    : plan === 'creator'
                      ? 'Creator (12-month vesting)'
                      : 'Creator Pro (6-month vesting)'}
                </div>
                <div className="mt-3 text-xs text-white/60">
                  {isCreator ? 'Earnings vest over time while your plan is active.' : 'Choose a plan to unlock payouts.'}
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm font-semibold">Earnings summary</div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-white/65">Total</span>
                  <span className="font-semibold text-zinc-100">{money(earnings.totalUsd)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span className="text-white/65">Available now</span>
                  <span className="font-semibold text-emerald-200">{money(earnings.availableUsd)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span className="text-white/65">Vesting over time</span>
                  <span className="font-semibold text-sky-200">{money(earnings.vestingUsd)}</span>
                </div>
                <div className="mt-2 text-xs text-white/60">
                  Available now is vested. Withdrawals are enabled after you connect Stripe.
                </div>
                <div className="mt-3">
                  <ProgressBar value={earnings.totalUsd - earnings.vestingUsd} max={earnings.totalUsd} />
                  <div className="mt-2 text-xs text-white/60">Next vesting unlock: {earnings.nextVestingDate}</div>
                </div>
              </div>

              <WithdrawalStatusBanner gate={withdrawGate} payoutsEnabled={payoutsEnabled} />
            </div>
          </div>

          <div className="md:col-span-1">
            {plan === 'none' ? (
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm font-semibold">Next steps</div>
                <div className="mt-2 text-sm text-white/75">Choose a plan to unlock payouts and connect Stripe.</div>
                <div className="mt-3 flex flex-col gap-2">
                  <Link
                    to="/settings/become-a-creator"
                    className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600 text-white border-sky-500/40 hover:bg-sky-500 hover:border-sky-400/50"
                  >
                    Become a Creator
                  </Link>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm font-semibold">Quick actions</div>
                <div className="mt-3 flex flex-col gap-2">
                  <Link
                    to="/settings/payouts"
                    className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600 text-white border-sky-500/40 hover:bg-sky-500 hover:border-sky-400/50"
                  >
                    View Payouts
                  </Link>

                  <button
                    onClick={async () => {
                      try {
                        const { url } = await createStripeConnectOnboarding()
                        window.location.assign(url)
                      } catch (e: any) {
                        window.alert(e?.response?.data?.detail || e?.message || 'Unable to start Stripe onboarding.')
                      }
                    }}
                    className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600/15 text-sky-100 border-sky-500/30 hover:bg-sky-600/25 hover:border-sky-400/40"
                    type="button"
                  >
                    {payoutsEnabled ? 'Stripe connected' : 'Connect Stripe'}
                  </button>

                  <SettingsButton variant="ghost" onClick={refresh}>
                    Refresh
                  </SettingsButton>
                </div>

                {!payoutsEnabled ? (
                  <div className="mt-3 text-xs text-white/60">Connect Stripe to enable withdrawals.</div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </SettingsCard>

      {isCreator ? (
        <SettingsCard title="Manage your plan" subtitle="Billing and plan controls.">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-sm text-white/70">Current plan</div>
              <div className="mt-1 text-base font-semibold">{isPro ? 'Creator Pro' : 'Creator'}</div>
              <div className="mt-1 text-sm text-white/70">
                Vesting: {meta.vestMonths} months | Posts: {meta.unlimitedPosts ? 'Unlimited' : `${meta.includedPosts}/month included`}
              </div>
            </div>
            <div className="flex gap-2">
              {plan === 'creator' ? (
                <Link
                  to="/settings/become-a-creator"
                  className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600 text-white border-sky-500/40 hover:bg-sky-500 hover:border-sky-400/50"
                >
                  Upgrade to Pro
                </Link>
              ) : (
                <Link
                  to="/settings/become-a-creator"
                  className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600/15 text-sky-100 border-sky-500/30 hover:bg-sky-600/25 hover:border-sky-400/40"
                >
                  Downgrade to Creator
                </Link>
              )}
              <Link
                to="/settings/become-a-creator"
                className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-red-500/15 text-red-200 border-red-500/30 hover:bg-red-500/25 hover:border-red-500/40"
              >
                Cancel plan
              </Link>
            </div>
          </div>
        </SettingsCard>
      ) : (
        <SettingsCard title="Become a Creator" subtitle="Unlock payouts, AI tools, and creator publishing features.">
          <div id="plans" className="space-y-3">
            <div className="text-sm text-white/75">
              Ready to publish as a creator? Choose a plan and connect Stripe to withdraw earnings.
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/settings/become-a-creator"
                className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600 text-white border-sky-500/40 hover:bg-sky-500 hover:border-sky-400/50"
              >
                Become a Creator
              </Link>
              <Link
                to="/settings/become-a-creator#compare"
                className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600/15 text-sky-100 border-sky-500/30 hover:bg-sky-600/25 hover:border-sky-400/40"
              >
                Compare plans
              </Link>
            </div>
            <div className="pt-2 border-t border-white/10">
              <div className="text-sm font-semibold">How payouts work</div>
              <ul className="mt-2 space-y-1 text-sm text-white/80">
                <li>
                  When people support your content with <span className="font-semibold">PIXE gifts</span>, it generates
                  earnings.
                </li>
                <li>Earnings vest while your plan is active.</li>
                <li>Vested earnings can be withdrawn through Stripe.</li>
                <li className="text-white/70">PIXE gifts stay on Pixeloop - payouts are made in USD.</li>
              </ul>
            </div>
          </div>
        </SettingsCard>
      )}
    </div>
  )
}
